/**
 * Prioritaetsstufen und ihre Farben (Roadmap V-03, fuer Termine seit D-03).
 *
 * Die Farben selbst stehen zentral als Tokens in `styles/tailwind.css`
 * (`--color-prio-*`, Leitlinie 7) - hier nur der Verweis darauf.
 */
import type { Goal, GoalPriorityKey, Priority, PriorityKey, Project, ProjectPriority, Task } from '@/domain/types';

export const PRIORITIES: { value: Priority; label: string; kurz: string }[] = [
  { value: 'dringend-wichtig', label: 'Dringend und wichtig', kurz: 'Sofort' },
  { value: 'wichtig', label: 'Wichtig, nicht dringend', kurz: 'Planen' },
  { value: 'dringend', label: 'Dringend, nicht wichtig', kurz: 'Abgeben' },
  { value: 'weder', label: 'Weder noch', kurz: 'Später' },
];

/** Projekte (V-04), spaeter auch Ziele (V-05): hoch, mittel, niedrig. */
export const PROJECT_PRIORITIES: { value: ProjectPriority; label: string }[] = [
  { value: 'hoch', label: 'Hoch' },
  { value: 'mittel', label: 'Mittel' },
  { value: 'niedrig', label: 'Niedrig' },
];

export const DEFAULT_PROJECT_PRIORITY: ProjectPriority = 'mittel';

/** Prioritaet eines Projekts; aeltere Projekte ohne Angabe gelten als "mittel". */
export function projectPriority(p: Pick<Project, 'priority'>): ProjectPriority {
  return p.priority ?? DEFAULT_PROJECT_PRIORITY;
}

/** Prioritaet eines Ziels (V-05); aeltere Ziele ohne Angabe gelten als "mittel". */
export function goalPriority(g: Pick<Goal, 'priority'>): ProjectPriority {
  return g.priority ?? DEFAULT_PROJECT_PRIORITY;
}

/** Farbschluessel einer Zielprioritaet - eigene, hellere Abstufung. */
export function goalPriorityKey(p: ProjectPriority): GoalPriorityKey {
  return `ziel-${p}`;
}

/** Die Zielstufen als Farbschluessel, z. B. fuer die Einstellungen. */
export const GOAL_PRIORITY_KEYS: { value: GoalPriorityKey; label: string }[] =
  PROJECT_PRIORITIES.map((p) => ({ value: goalPriorityKey(p.value), label: p.label }));

/** Sortierrang: hoch zuerst. */
export function projectPriorityRank(p: ProjectPriority): number {
  return PROJECT_PRIORITIES.findIndex((x) => x.value === p);
}

/**
 * CSS-Wert der Prioritaetsfarbe - ein Token, nie ein Farbwert im Code.
 * Selbst gewaehlte Farben (Einstellungen) ueberschreiben das Token zentral,
 * siehe `modules/settings/PriorityColorsApplier.tsx`.
 */
export function priorityColor(p: PriorityKey): string {
  return `var(--color-prio-${p})`;
}

export function priorityLabel(p: PriorityKey): string {
  const aufgabe = PRIORITIES.find((x) => x.value === p);
  if (aufgabe) return aufgabe.label;
  const projekt = PROJECT_PRIORITIES.find((x) => x.value === p);
  if (projekt) return `${projekt.label} (Projekt)`;
  const ziel = GOAL_PRIORITY_KEYS.find((x) => x.value === p);
  return ziel ? `${ziel.label} (Ziel)` : p;
}

/**
 * Prioritaet einer Aufgabe aus den Eisenhower-Achsen (V-03).
 *
 * Aufgaben in der Inbox sind noch nicht eingeordnet - sie haben keine
 * Prioritaet, nicht "weder noch". Sonst truege jede frisch notierte Aufgabe
 * einen grauen Punkt, der etwas behauptet, das niemand entschieden hat.
 */
export function taskPriority(t: Pick<Task, 'important' | 'urgent' | 'status'>): Priority | undefined {
  if (t.status === 'inbox') return undefined;
  if (t.important && t.urgent) return 'dringend-wichtig';
  if (t.important) return 'wichtig';
  if (t.urgent) return 'dringend';
  return 'weder';
}
